import _ from 'underscore';
import { getEnumValuefromLegacy } from './enum';

const operatorMap = {
  $contains: 'Contains',
  $doesnotcontain: 'DoesNotContain',
  $startswith: 'BeginsWith',
  $endswith: 'EndsWith',
  $eq: 'Equal',
  $neq: 'NotEqual',
  $gt: 'Greater',
  $gte: 'GreaterOrEqual',
  $lt: 'Less',
  $lte: 'LessOrEqual',
  $in: 'EnumSelection',
  $containsall: 'ContainsAll',
  $containsany: 'ContainsAny',
  $containsnone: 'ContainsNone',
};
const reverseOperatorMap = _.invert(operatorMap);

function toLegacyColumnName(columnName, schema) {
  let column = columnName;
  if (schema) {
    column = schema.toAlias(columnName, 'legacy');
  }
  return column.split('/').pop();
}

function fromLegacyColumnName(columnName, schema) {
  if (!schema) {
    return columnName;
  }
  let column = schema.fromAlias(columnName, 'legacy');
  if (!schema.getFilterProperty(column)) {
    column = _.find(schema.getPropertyKeys(), key => key.split('/').pop() === column);
  }
  return column;
}

function toMongoPredicate(legacyExpression, schema) {
  const column = fromLegacyColumnName(legacyExpression.SelectedColumn, schema);
  const legacyOperator = legacyExpression.SelectedOperator;
  const mongoOperator = reverseOperatorMap[legacyOperator];
  let value = mongoOperator === '$in' || mongoOperator === '$containsall' || mongoOperator === '$containsany' || mongoOperator === '$containsnone' ? legacyExpression.Values : legacyExpression.Values[0];
  if (schema) {
    const prop = schema.getFilterProperty(column);
    if (prop && prop.type) {
      switch (prop.type) {
        case 'datetime':
          value = new Date(value);
          break;
        case 'number':
          value = parseFloat(value);
          break;
        case 'integer':
          value = parseInt(value, 10);
          break;
        case 'boolean':
          value = value === 1;
          break;
        default:
          break;
      }
    }
    if (prop && (prop.enum || prop.groupedItems)) {
      value = _.map(
        legacyExpression.Values,
        val => getEnumValuefromLegacy(val, prop.enumType || column)
      );
    }
  }
  return { [column]: { [mongoOperator]: value } };
}

function toLegacyExpression(field, expression, schema) {
  const mongoOperator = _.keys(expression)[0];
  const mongoValue = _.values(expression)[0];
  return {
    SelectedColumn: toLegacyColumnName(field, schema),
    SelectedOperator: operatorMap[mongoOperator],
    Values: _.isArray(mongoValue) ? mongoValue : [mongoValue],
  };
}

/**
 * Convert normalized filter object to legacy expression.
 * @param {Object} filters - normalized filter object.
 * @param {number} depth - filter's depth.
 * @param {Object} schema - FilterSchema instance.
 * @returns {Object[]} - legacy expressions.
 */
export function convertToLegacy(filters, depth, schema) {
  if (!depth || !filters) {
    throw new Error('Depth or filter object is not defined.');
  }
  if (depth > 2) {
    throw new Error('Filter of depth > 2 can not be converted into legacy filter expression array.');
  }
  if (_.isEmpty(filters)) {
    return [];
  }

  const mongoExpressions = depth === 1 ? [filters] : filters.$and || filters.$or;
  return _.map(
    mongoExpressions,
    expr => toLegacyExpression(_.keys(expr)[0], _.values(expr)[0], schema)
  );
}

/**
 * Convert legacy expression to normalized filter query object.
 * @param {Object[]} legacyExpressions - the legacy expression.
 * @param {Object} schema - FilterSchema instance.
 * @returns {Object} - the normalized filter object.
 */
export function parseFromLegacy(legacyExpressions, schema) {
  if (!_.isArray(legacyExpressions)) {
    throw new Error('legacyExpressions must be an array of legacy expressions.');
  }
  if (_.isEmpty(legacyExpressions)) {
    return {};
  }
  if (legacyExpressions.length === 1) {
    return toMongoPredicate(legacyExpressions[0], schema);
  }
  return { $and: _.map(legacyExpressions, expr => toMongoPredicate(expr, schema)) };
}
